import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiFetch } from "@/lib/api-client";
import { useTranslation } from "react-i18next";

interface Brand {
  id: string;
  name: string;
  slug: string;
}

interface Membership {
  id: string;
  role: string;
  // null when the membership references a brand that was since deleted — the
  // admin endpoint returns brand: null for those.
  brand: Brand | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | null;
  userEmail: string;
}

const ROLES = ["OWNER", "ADMIN", "CREATOR", "REVIEWER", "SCHEDULER", "VIEWER"];

export function ManageUserBrandsDialog({ open, onOpenChange, userId, userEmail }: Props) {
  const { t } = useTranslation("admin");
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [allBrands, setAllBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(false);
  const [brandToAdd, setBrandToAdd] = useState<string>("");
  const [roleToAdd, setRoleToAdd] = useState<string>("CREATOR");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    Promise.all([
      apiFetch<Membership[]>(`/admin/users/${userId}/brands`),
      apiFetch<Brand[]>(`/admin/brands`),
    ])
      .then(([m, b]) => {
        setMemberships(m);
        setAllBrands(b);
      })
      .catch(() => toast.error(t("manageBrands.toast.loadError")))
      .finally(() => setLoading(false));
  }, [open, userId, t]);

  // Orphaned memberships (brand deleted) aren't actionable here — hide them.
  const liveMemberships = memberships.filter((m) => m.brand !== null);
  const availableBrands = allBrands.filter(
    (b) => !liveMemberships.some((m) => m.brand?.id === b.id)
  );

  async function handleAdd() {
    if (!userId || !brandToAdd) return;
    setAdding(true);
    try {
      const res = await apiFetch<Membership>(
        `/admin/users/${userId}/brands`,
        {
          method: "POST",
          body: JSON.stringify({ brandId: brandToAdd, role: roleToAdd }),
        }
      );
      setMemberships((prev) => [...prev, res]);
      setBrandToAdd("");
      toast.success(t("manageBrands.toast.granted"));
    } catch (e: any) {
      toast.error(e?.message ?? t("manageBrands.toast.grantError"));
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(brandId: string) {
    if (!userId) return;
    if (!confirm(t("manageBrands.confirmRevoke"))) return;
    const prev = memberships;
    setMemberships((m) => m.filter((x) => x.brand?.id !== brandId));
    try {
      await apiFetch(`/admin/users/${userId}/brands/${brandId}`, {
        method: "DELETE",
      });
      toast.success(t("manageBrands.toast.revoked"));
    } catch (e: any) {
      setMemberships(prev);
      toast.error(e?.message ?? t("manageBrands.toast.revokeError"));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("manageBrands.title")}</DialogTitle>
          <DialogDescription>{userEmail}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-muted-foreground py-4">{t("manageBrands.loading")}</p>
        ) : (
          <div className="space-y-4">
            <section className="space-y-2">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {t("manageBrands.currentAccess")}
              </h3>
              {liveMemberships.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("manageBrands.noAccess")}</p>
              ) : (
                <ul className="space-y-1">
                  {liveMemberships.map((m) => (
                    <li
                      key={m.id}
                      className="flex items-center justify-between rounded border border-border px-3 py-2 text-sm"
                    >
                      <span>
                        <span className="font-medium">{m.brand!.name}</span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          {m.role}
                        </span>
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemove(m.brand!.id)}
                      >
                        {t("manageBrands.revoke")}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {availableBrands.length > 0 && (
              <section className="space-y-2 border-t border-border pt-4">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t("manageBrands.grantAccess")}
                </h3>
                <div className="flex items-center gap-2">
                  <Select value={brandToAdd} onValueChange={setBrandToAdd}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder={t("manageBrands.selectBrand")} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableBrands.map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={roleToAdd} onValueChange={setRoleToAdd}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r}>
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleAdd}
                    disabled={!brandToAdd || adding}
                    size="sm"
                  >
                    {adding ? t("manageBrands.adding") : t("manageBrands.add")}
                  </Button>
                </div>
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
